// poiesis/src/auth/session.ts
// The ONE client-side session store (SEN-1336 A2 identity). A session is the
// bearer token + the member it belongs to, persisted in localStorage under
// `poiesis.session`. Everything auth-shaped reads it from here: the fetch
// wrapper (which header to send), the shell (who is signed in), the gate
// (sign-in vs app).
//
// Events (window-level, so module singletons and React trees stay in sync):
//  - `poiesis:session`      — the stored session changed (set or cleared)
//  - `poiesis:unauthorized` — a request came back 401; the app must show sign-in
import { useSyncExternalStore } from 'react'

export const SESSION_KEY = 'poiesis.session'
export const SESSION_EVENT = 'poiesis:session'
export const UNAUTHORIZED_EVENT = 'poiesis:unauthorized'

export interface SessionMember {
  member_id: string
  tenant_id: string
  email: string
  name: string | null
  is_admin: boolean
  /** Tenant reference the backend now returns with login/accept-invite (SEN-1336 addendum). */
  tenant_slug?: string | null
  tenant_name?: string | null
}

export interface Session {
  token: string
  /** ISO timestamp or null (api keys never expire; login sessions do). */
  expires_at: string | null
  member: SessionMember
  /** The tenant slug this session was opened for (login typed it; accept-invite
   *  carries it in the link). Needed because the app addresses everything by slug. */
  tenant_slug: string
}

let cache: Session | null | undefined // undefined = not read yet

export function isExpired(s: Pick<Session, 'expires_at'>, now: number = Date.now()): boolean {
  if (!s.expires_at) return false
  const t = new Date(s.expires_at).getTime()
  return !Number.isNaN(t) && t <= now
}

function read(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Session
    if (!parsed || typeof parsed.token !== 'string' || !parsed.member) return null
    // Boot-time expiry check: a session past expires_at is dropped instead of
    // being sent and bounced with a 401.
    if (isExpired(parsed)) { try { localStorage.removeItem(SESSION_KEY) } catch { /* ignore */ } return null }
    return parsed
  } catch {
    return null
  }
}

function emit(): void {
  try { window.dispatchEvent(new Event(SESSION_EVENT)) } catch { /* no window (node) */ }
}

export function getSession(): Session | null {
  if (cache === undefined) cache = read()
  return cache
}

export function setSession(s: Session): void {
  cache = s
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(s)) } catch { /* privacy mode — in-memory only */ }
  emit()
}

export function clearSession(): void {
  cache = null
  try { localStorage.removeItem(SESSION_KEY) } catch { /* ignore */ }
  emit()
}

/**
 * SIGN-OUT HAS BEGUN AND THIS PAGE IS LEAVING (SEN-1781 c12).
 *
 * `signOut()` clears the session and then navigates. Between those two
 * statements React re-renders with no session, and anything whose effect is
 * keyed on "signed out" starts work the navigation is about to abort. CI run
 * 34517375849 lost that coin-flip: `GET /dev/tenants` was recorded as a 404
 * at 11795 ms and as `net::ERR_ABORTED` at 11797 ms — two milliseconds apart
 * — and the undeclared 404 failed the gate.
 *
 * A one-way latch rather than a flag: nothing un-sets it, because the only
 * thing that ends a sign-out is the page going away. `_resetSignOut` exists
 * for tests and is not part of the sign-out path.
 */
let signingOut = false
export function beginSignOut(): void { signingOut = true }
export function isSigningOut(): boolean { return signingOut }
/** Test-only. */
export function _resetSignOut(): void { signingOut = false }

/** Signal "the server no longer accepts our credentials" — the gate shows sign-in. */
export function notifyUnauthorized(): void {
  try { window.dispatchEvent(new Event(UNAUTHORIZED_EVENT)) } catch { /* no window */ }
}

function subscribe(cb: () => void): () => void {
  window.addEventListener(SESSION_EVENT, cb)
  // Another tab signed in/out → localStorage `storage` event; drop the cache and re-read.
  const onStorage = (e: StorageEvent) => { if (e.key === SESSION_KEY || e.key === null) { cache = undefined; cb() } }
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(SESSION_EVENT, cb)
    window.removeEventListener('storage', onStorage)
  }
}

/** React subscription to the current session (null when signed out). */
export function useSession(): Session | null {
  return useSyncExternalStore(subscribe, getSession, () => null)
}

/** Test seam: forget the in-memory cache so the next read hits localStorage. */
export function _resetSessionCache(): void {
  cache = undefined
}
